import { safeSignInReturnPath } from '../navigation/signInReturnPath.js';
import { sessionContextSchema } from '@solidops/shared/identity';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { canonicalErrorSchema } from '@solidops/shared/common';
import type { CanonicalError, ManagementErrorDetails } from '@solidops/shared/common';
import type { z } from 'zod';
import { globals } from '../../config/globals.js';

const sessionPath = `${globals.apiBaseUrl}/session`;

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly fieldErrors?: CanonicalError['fieldErrors'],
    public readonly details?: ManagementErrorDetails,
  ) {
    super(message);
  }
}
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) queryClient.clear();
    },
  }),
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: true } },
});

export function safeSignInPath(): string {
  if (typeof window === 'undefined') return '/sign-in';
  const path = window.location.pathname;
  const target = safeSignInReturnPath(
    path === '/account/contacts/verify'
      ? path
      : path + (window.location.search ?? '') + (window.location.hash ?? ''),
  );
  return target ? `/sign-in?returnTo=${encodeURIComponent(target)}` : '/sign-in';
}

export function validatedFetch<T>(
  url: string,
  options?: RequestInit & { schema?: z.ZodType<unknown> },
): Promise<T>;
export async function validatedFetch(
  url: string,
  options: RequestInit & { schema?: z.ZodType<unknown> } = {},
): Promise<unknown> {
  const { schema, ...request } = options;
  const response = await authenticatedResponse(url, request);
  request.signal?.throwIfAborted();
  if (response.status === 204) return undefined;
  if (!schema) throw new Error('Response validator missing.');
  if (!(response.headers.get('content-type') ?? '').toLowerCase().includes('application/json'))
    throw new Error('Expected JSON response.');
  const raw: unknown = await response.json();
  request.signal?.throwIfAborted();
  const value = schema.parse(raw);
  if (url === sessionPath) {
    const previous = queryClient.getQueryData<{ user: { role: string | null }; sessionId: string }>(
      [sessionPath],
    );
    const next = sessionContextSchema.parse(value);
    if (
      previous &&
      (previous.user.role !== next.user.role || previous.sessionId !== next.sessionId)
    )
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== sessionPath });
  }
  return value;
}

async function authenticatedResponse(url: string, request: RequestInit): Promise<Response> {
  request.signal?.throwIfAborted();
  const response = await fetch(url, { ...request, credentials: 'same-origin', cache: 'no-store' });
  request.signal?.throwIfAborted();
  if (response.status === 401) {
    queryClient.clear();
    if (typeof window !== 'undefined' && window.location.pathname !== '/sign-in')
      window.location.assign(safeSignInPath());
  }
  if (response.status === 403) {
    void queryClient.invalidateQueries({ queryKey: [sessionPath] });
  }
  if (!response.ok) {
    const error = canonicalErrorSchema.strip().safeParse(await response.json().catch(() => null));
    request.signal?.throwIfAborted();
    throw new ApiError(
      response.status,
      error.success ? error.data.message : 'Request could not be completed.',
      error.success ? error.data.code : undefined,
      error.success ? error.data.fieldErrors : undefined,
      error.success ? error.data.details : undefined,
    );
  }
  return response;
}

/** Binary consumers share the same current-request authentication and structured error handling. */
export async function validatedBlobFetch(
  url: string,
  request: RequestInit,
  expected: { readonly mimeType: string; readonly maximumBytes: number },
): Promise<Blob> {
  const response = await authenticatedResponse(url, request);
  let length: number;
  try {
    request.signal?.throwIfAborted();
    length = binaryResponseLength(response, expected);
  } catch (error) {
    await response.body?.cancel();
    throw error;
  }
  if (!response.body) throw new Error('Binary response body missing.');
  const reader = response.body.getReader();
  const signal = request.signal;
  const abort = (): void => {
    void reader.cancel(signal?.reason).catch(() => undefined);
  };
  signal?.addEventListener('abort', abort, { once: true });
  const chunks: BlobPart[] = [];
  let total = 0;
  try {
    while (true) {
      const part = await reader.read();
      signal?.throwIfAborted();
      if (part.done) break;
      total += part.value.byteLength;
      if (total > expected.maximumBytes || total > length) {
        await reader.cancel();
        throw new Error('Binary response exceeds its limit.');
      }
      chunks.push(new Uint8Array(part.value));
    }
    if (total !== length) throw new Error('Incomplete binary response.');
    signal?.throwIfAborted();
    return new Blob(chunks, { type: expected.mimeType });
  } finally {
    signal?.removeEventListener('abort', abort);
    reader.releaseLock();
  }
}

function binaryResponseLength(
  response: Response,
  expected: { readonly mimeType: string; readonly maximumBytes: number },
): number {
  if (response.status !== 200) throw new Error('Expected a complete binary response.');
  const length = response.headers.get('content-length');
  if (
    (response.headers.get('content-type') ?? '').split(';', 1)[0]?.trim().toLowerCase() !==
    expected.mimeType
  )
    throw new Error('Unexpected binary response type.');
  if (
    !(response.headers.get('cache-control') ?? '')
      .split(',')
      .some((directive) => directive.trim().toLowerCase() === 'no-store')
  )
    throw new Error('Private binary response must not be stored.');
  if (
    !length ||
    !/^\d+$/.test(length) ||
    !Number.isSafeInteger(Number(length)) ||
    Number(length) < 1 ||
    Number(length) > expected.maximumBytes
  )
    throw new Error('Invalid binary response length.');
  return Number(length);
}
